import _ from 'lodash';
import chartColors from '../../../shared/js/lib/chartColors';
import moment from 'moment';
import wrap from 'word-wrap';


const getDuration = (personObservation) => {
	const seconds = moment(personObservation.ended_at).diff(moment(personObservation.started_at), 'seconds');

	return seconds / 60;
};

/** Converts a long string into an array of lines of `maxWidth` or less **/
const splitLabel = (stringToWrap, maxWidth = 28, shouldCutLongWords = false) => {
	const wrappedString = wrap(stringToWrap, { width: maxWidth });

	return wrappedString.split('\n');
};

const getMovesChartData = (labels, backgroundColors, values) => {
	const wrappedLabels = labels.map((label) => splitLabel(label));

	return {
		labels: wrappedLabels,
		datasets: [
			{
				label: 'Occurrences',
				backgroundColor: backgroundColors,
				data: values
			}
		]
	};
};

const formatDecimal = (value) => {
	if (!value) {
		return '';
	} else {
		return parseFloat(value.toFixed(1));
	}
};

module.exports = {
	getDuration,
	formatDecimal,

	getDoughnutChartData: function(personObservations, association) {
		let minutes = {},
			studentIds = [];

		// If this chart contains multiple students, the student values should be averaged
		personObservations.map((personObservation) => {
			if (personObservation.student_id && !studentIds.includes(personObservation.student_id)) {
				studentIds.push(personObservation.student_id);
			}
		});

		personObservations.map((personObservation) => {
			let duration = getDuration(personObservation);

			if (personObservation.student_id) {
				duration = duration / studentIds.length;
			}

			if (duration > 0) {
				if (typeof(minutes[personObservation[association].description]) === 'undefined') {
					minutes[personObservation[association].description] = duration;
				} else {
					minutes[personObservation[association].description] += duration;
				}
			}
		});

		const keys = Object.keys(minutes).sort();
		const values = keys.map((key) => minutes[key]);

		return {
			labels: keys,
			datasets: [{
				data: values,
				backgroundColor: Object.values(chartColors),
				hoverBackgroundColor: Object.values(chartColors)
			}]
		};
	},

	getDoughnutChartOptions: function() {
		return {
			responsive: true,
			maintainAspectRatio: false,

			title: {
				display: false
			},

			legend: {
				position: 'bottom',
				labels: {
					fontSize: 14
				}
			},

			elements: {
				arc: {
					borderWidth: 0
				}
			},

			plugins: {
				datalabels: {
					color: '#222',
					font: {
						weight: 'bold',
						size: 18,
					},
					formatter: formatDecimal
				}
			},

			tooltips: {
				callbacks: {
					title: (tooltipItems, data) => {
						return data.labels[tooltipItems[0].index];
					},
					label: (tooltipItem, data) => {
						return formatDecimal(data.datasets[0].data[tooltipItem.index]);
					}
				}
			}
		};
	},

	getMovesChartOptions: (max = 10) => {
		return {
			responsive: true,
			maintainAspectRatio: false,
			legend: {
				display: false
			},

			title: {
				display: false
			},

			plugins: {
				datalabels: {
					color: '#333',
					font: {
						weight: 'bold',
						size: 16
					},
					render: 'value',
					anchor: 'center',
					align: 'center',
					clamp: true,
					formatter: function(value) {
						return value === 0 ? '' : value;
					}
				}
			},

			scales: {
				xAxes: [{
					ticks: {
						min: 0,
						max: max
					}
				}]
			},

			tooltips: {
				callbacks: {
					title: (tooltipItems, data) => {
						return tooltipItems[0].label.replace(/ , /, '');
					}
				}
			}
		};
	},

	getBarChartData: function(personObservations, association) {
		let minutes = {};

		personObservations.map((personObservation) => {
			const duration = getDuration(personObservation);

			if (duration > 0) {
				if (typeof(minutes[personObservation[association].description]) === 'undefined') {
					minutes[personObservation[association].description] = duration;
				} else {
					minutes[personObservation[association].description] += duration;
				}
			}
		});

		return {
			labels: Object.keys(minutes),
			datasets: [{
				data: Object.values(minutes),
				backgroundColor: Object.values(chartColors),
				hoverBackgroundColor: Object.values(chartColors)
			}]
		};
	},

	getLiteracyBreakdownChartData: function(personObservations) {
		let minutes = {};

		personObservations.map((personObservation) => {
			if (personObservation.observation_subject.description === 'Literacy') {
				const duration = getDuration(personObservation);

				if (duration > 0) {
					let management,
						context,
						instructionType = personObservation.observation_instruction_type.abbreviation;

					switch (personObservation.observation_management.description) {
						case 'Teacher':
							management = 'T/CM-';
							break;
						case 'Peers':
							management = 'PM-';
							break;
						case 'Alone':
							management = 'CM-';
							break;
					}

					switch (personObservation.observation_context.description) {
						case 'Whole class':
							context = 'WC-';
							break;
						case 'Small group':
							context = 'SG-';
							break;
						case 'Individual':
							context = 'Indiv-';
							break;
					}

					const abbreviation = management + context + instructionType;

					if (typeof(minutes[abbreviation]) === 'undefined') {
						minutes[abbreviation] = duration;
					} else {
						minutes[abbreviation] += duration;
					}

					if (personObservation.student_id && personObservation.student_behavior_type.description === 'Off task') {
						if (minutes['Off task']) {
							minutes['Off task'] += duration;
						} else {
							minutes['Off task'] = duration;
						}
					}
				}
			}
		});

		return {
			labels: Object.keys(minutes),
			datasets: [{
				data: Object.values(minutes),
				backgroundColor: Object.values(chartColors),
				hoverBackgroundColor: Object.values(chartColors)
			}]
		};
	},

	getMathBreakdownChartData: function(personObservations) {
		let minutes = {};

		personObservations.map((personObservation) => {
			if (personObservation.observation_subject.description === 'Math') {
				const duration = getDuration(personObservation);

				if (duration > 0) {
					const instructionType = personObservation.observation_instruction_type.description;

					if (typeof(minutes[instructionType]) === 'undefined') {
						minutes[instructionType] = duration;
					} else {
						minutes[instructionType] += duration;
					}

					if (personObservation.student_id && personObservation.student_behavior_type.description === 'Off task') {
						if (minutes['Off task']) {
							minutes['Off task'] += duration;
						} else {
							minutes['Off task'] = duration;
						}
					}
				}
			}
		});

		return {
			labels: Object.keys(minutes),
			datasets: [{
				data: Object.values(minutes),
				backgroundColor: Object.values(chartColors),
				hoverBackgroundColor: Object.values(chartColors)
			}]
		};
	},

	getOtherBreakdownChartData: function(personObservations) {
		let minutes = {};

		personObservations.map((personObservation) => {
			const subject = personObservation.observation_subject.description;

			if (subject !== 'Literacy' && subject !== 'Math') {
				const duration = getDuration(personObservation);

				if (duration > 0) {
					const subject = personObservation.observation_subject.description;

					if (typeof(minutes[subject]) === 'undefined') {
						minutes[subject] = duration;
					} else {
						minutes[subject] += duration;
					}

					if (personObservation.student_id && personObservation.student_behavior_type.description === 'Off task') {
						if (minutes['Off task']) {
							minutes['Off task'] += duration;
						} else {
							minutes['Off task'] = duration;
						}
					}
				}
			}
		});

		return {
			labels: Object.keys(minutes),
			datasets: [{
				data: Object.values(minutes),
				backgroundColor: Object.values(chartColors),
				hoverBackgroundColor: Object.values(chartColors)
			}]
		};
	},

	getBarChartOptions: function(max) {
		return {
			responsive: true,
			maintainAspectRatio: false,
			legend: {
				display: false
			},

			title: {
				display: false
			},

			plugins: {
				datalabels: {
					color: '#333',
					font: {
						weight: 'bold',
						size: 16
					},
					render: 'value',
					anchor: 'center',
					align: 'center',
					formatter: formatDecimal
				}
			},

			scales: {
				xAxes: [{
					ticks: {
						min: 0,
						max: max
					}
				}]
			},

			tooltips: {
				callbacks: {
					title: (tooltipItems, data) => {
						return tooltipItems[0].label.replace(/ , /, '');
					},
					label: (tooltipItem, data) => {
						return formatDecimal(tooltipItem.xLabel);
					}
				}
			}
		};
	},

	getStudentMovesChartData: function(studentEventTypes, studentObservations) {
		const eventTypeIdsToHide = [8, 9, 13, 14];
		const eventTypeNames = _.sortBy(studentEventTypes, 'display_order')
			.filter((eventType) => !eventTypeIdsToHide.includes(eventType.id))
			.map((eventType) => eventType.description);

		let eventCounts = [];

		eventTypeNames.map((eventTypeName) => {
			let count = 0;

			studentObservations.map((studentObservation) => {
				studentObservation.student_observation_events.map((event) => {
					if (event.student_event_type.description === eventTypeName) {
						count += 1;
					}
				});
			});

			eventCounts.push(count);
		});

		const backgroundColors = [
			chartColors.yellow,
			chartColors.yellow,
			chartColors.yellow,
			chartColors.green,
			chartColors.green,
			chartColors.green,
			chartColors.green,
			chartColors.green,
			chartColors.blue,
			chartColors.blue
		];

		return getMovesChartData(eventTypeNames, backgroundColors, eventCounts);
	},

	getTeacherMovesChartData: function(teacherEventTypes, teacherObservations) {
		const eventTypeIdsToHide = [14, 15, 16, 17];
		const eventTypeNames = _.sortBy(teacherEventTypes, 'display_order')
			.filter((eventType) => !eventTypeIdsToHide.includes(eventType.id))
			.map((eventType) => eventType.description);

		let eventCounts = [];

		eventTypeNames.map((eventTypeName) => {
			let count = 0;

			teacherObservations.map((teacherObservation) => {
				teacherObservation.teacher_observation_events.map((event) => {
					if (event.teacher_event_type.description === eventTypeName) {
						count += 1;
					}
				});
			});

			eventCounts.push(count);
		});

		const backgroundColors = [
			chartColors.red,
			chartColors.red,
			chartColors.red,
			chartColors.yellow,
			chartColors.yellow,
			chartColors.green,
			chartColors.green,
			chartColors.green,
			chartColors.green,
			chartColors.blue,
			chartColors.blue,
			chartColors.blue,
			chartColors.blue
		];

		return getMovesChartData(eventTypeNames, backgroundColors, eventCounts);
	}
};



// WEBPACK FOOTER //
// ./frontend/olos/js/lib/dashboardCharts.js